export type PlayerScheduleMember = { id: string; name: string; hostPlayerId?: string; isGuest?: boolean };

type MatchResponseItem = {
  id: string;
  kind: 'match';
  ownAvailability: 'available' | 'maybe' | 'not_available' | 'unknown';
  availabilityCounts: Record<string, number>;
  availabilityGroups: Record<string, PlayerScheduleMember[]>;
};

type EventResponseItem = {
  id: string;
  kind: 'event';
  ownRsvp: 'Going' | 'Maybe' | 'Not Going' | 'No Response';
  ownGuestCount?: number;
  rsvpCounts: Record<string, number>;
  rsvpGroups: Record<string, PlayerScheduleMember[]>;
  guestGroups?: Record<string, PlayerScheduleMember[]>;
  guestSummary?: { guests: number; totalAttending: number; maybeGuests: number; potentialAdditional: number };
};

type ResponseItem = MatchResponseItem | EventResponseItem;
type ResponseContext<M extends MatchResponseItem, E extends EventResponseItem> = {
  player: { id: string; displayName: string };
  schedule: { matches: M[]; events: E[] };
};

function updatedGroups(
  groups: Record<string, PlayerScheduleMember[]>,
  previousKey: string,
  nextKey: string,
  player: PlayerScheduleMember,
) {
  const withoutPlayer = (members: PlayerScheduleMember[] = []) => members.filter((member) => member.id !== player.id);
  const nextGroups = { ...groups };
  nextGroups[previousKey] = withoutPlayer(groups[previousKey]);
  nextGroups[nextKey] = [...withoutPlayer(groups[nextKey]), player]
    .sort((left, right) => left.name.localeCompare(right.name));
  return nextGroups;
}

function updatedCounts(counts: Record<string, number>, previousKey: string, nextKey: string) {
  if (previousKey === nextKey) return counts;
  return {
    ...counts,
    [previousKey]: Math.max(0, Number(counts[previousKey] || 0) - 1),
    [nextKey]: Number(counts[nextKey] || 0) + 1,
  };
}

export function updatePlayerScheduleResponse<T extends ResponseContext<M, E>, M extends MatchResponseItem, E extends EventResponseItem>(
  context: T,
  item: M | E,
  status: string,
  requestedGuestCount?: number,
): T {
  const player = { id: context.player.id, name: context.player.displayName };

  if (item.kind === 'match') {
    const nextStatus = status as MatchResponseItem['ownAvailability'];
    const updateMatch = (match: M): M => {
      if (match.id !== item.id || match.ownAvailability === nextStatus) return match;
      return {
        ...match,
        ownAvailability: nextStatus,
        availabilityCounts: updatedCounts(match.availabilityCounts, match.ownAvailability, nextStatus),
        availabilityGroups: updatedGroups(match.availabilityGroups, match.ownAvailability, nextStatus, player),
      };
    };
    return {
      ...context,
      schedule: { ...context.schedule, matches: context.schedule.matches.map(updateMatch) },
    } as T;
  }

  const nextStatus = status as EventResponseItem['ownRsvp'];
  const keyFor = (value: EventResponseItem['ownRsvp']) => ({
    Going: 'going',
    Maybe: 'maybe',
    'Not Going': 'notGoing',
    'No Response': 'noResponse',
  }[value]);
  const updateEvent = (event: E): E => {
    if (event.id !== item.id) return event;
    const nextGuestCount = nextStatus === 'Going' || nextStatus === 'Maybe'
      ? Math.max(0, Math.min(10, Math.trunc(requestedGuestCount ?? event.ownGuestCount ?? 0)))
      : 0;
    if (event.ownRsvp === nextStatus && Number(event.ownGuestCount || 0) === nextGuestCount) return event;
    const previousKey = keyFor(event.ownRsvp);
    const nextKey = keyFor(nextStatus);
    const nextGroups = updatedGroups(event.rsvpGroups, previousKey, nextKey, player);
    const withoutOwnGuests = (members: PlayerScheduleMember[] = []) => members.filter((member) => member.hostPlayerId !== player.id);
    const guestGroups = {
      going: withoutOwnGuests(event.guestGroups?.going),
      maybe: withoutOwnGuests(event.guestGroups?.maybe),
    };
    const guestKey = nextStatus === 'Going' ? 'going' : nextStatus === 'Maybe' ? 'maybe' : null;
    if (guestKey) {
      guestGroups[guestKey] = [
        ...guestGroups[guestKey],
        ...Array.from({ length: nextGuestCount }, (_, index) => ({
          id: `guest:${player.id}:${index + 1}`,
          name: `${player.name}'s Guest ${index + 1}`,
          hostPlayerId: player.id,
          isGuest: true,
        })),
      ];
    }
    const counts = updatedCounts(event.rsvpCounts, previousKey, nextKey);
    return {
      ...event,
      ownRsvp: nextStatus,
      ownGuestCount: nextGuestCount,
      rsvpCounts: counts,
      rsvpGroups: nextGroups,
      guestGroups,
      guestSummary: {
        guests: guestGroups.going.length,
        totalAttending: Number(counts.going || 0) + guestGroups.going.length,
        maybeGuests: guestGroups.maybe.length,
        potentialAdditional: Number(counts.maybe || 0) + guestGroups.maybe.length,
      },
    };
  };
  return {
    ...context,
    schedule: { ...context.schedule, events: context.schedule.events.map(updateEvent) },
  } as T;
}
